import { DerivedEdge } from 'hooks/data/datasets/V4/supplyChain/supplyChainUtils';

import { SUPPLY_H_GAP, SUPPLY_NODE_WIDTH, SUPPLY_V_SPACING } from './constants';

export const computeSupplyChainPositions = (
  nodeIds: string[],
  edgesData: DerivedEdge[],
  rootId: string,
  nodeHeight: number
): { positions: Record<string, { x: number; y: number }>; depth: Record<string, number> } => {
  const inputsOf: Record<string, string[]> = {};
  const outputsOf: Record<string, string[]> = {};
  const functionalInputsOf: Record<string, Set<string>> = {};
  const functionalOutputsOf: Record<string, Set<string>> = {};
  for (const id of nodeIds) {
    inputsOf[id] = [];
    outputsOf[id] = [];
    functionalInputsOf[id] = new Set();
    functionalOutputsOf[id] = new Set();
  }
  for (const edge of edgesData) {
    if (inputsOf[edge.target_id] !== undefined) {
      inputsOf[edge.target_id].push(edge.source_id);
      if (edge.is_functional) functionalInputsOf[edge.target_id].add(edge.source_id);
    }
    if (outputsOf[edge.source_id] !== undefined) {
      outputsOf[edge.source_id].push(edge.target_id);
      if (edge.is_functional) functionalOutputsOf[edge.source_id].add(edge.target_id);
    }
  }

  const centreFunctional = (siblings: string[], functional: Set<string> | undefined): string[] => {
    const from = siblings.findIndex((id) => functional?.has(id) ?? false);
    const to = Math.floor((siblings.length - 1) / 2);
    if (from < 0 || from === to || siblings.every((id) => functional?.has(id))) return siblings;
    const reordered = siblings.filter((_, i) => i !== from);
    reordered.splice(to, 0, siblings[from]);
    return reordered;
  };

  const depth: Record<string, number> = { [rootId]: 0 };
  const queue = [rootId];
  while (queue.length > 0) {
    const id = queue.shift() as string;
    const d = depth[id];
    for (const inputId of inputsOf[id] ?? []) {
      if (depth[inputId] === undefined) {
        depth[inputId] = d + 1;
        queue.push(inputId);
      }
    }
    for (const outputId of outputsOf[id] ?? []) {
      if (depth[outputId] === undefined) {
        depth[outputId] = d - 1;
        queue.push(outputId);
      }
    }
  }

  const maxDepth = Math.max(...Object.values(depth), 0);

  const rowOf: Record<string, number> = {};
  rowOf[rootId] = 0;

  for (let d = 0; d < maxDepth; d++) {
    for (const id of nodeIds) {
      if ((depth[id] ?? 0) !== d) continue;
      const children = centreFunctional(
        (inputsOf[id] ?? []).filter((childId) => (depth[childId] ?? 0) === d + 1),
        functionalInputsOf[id]
      );
      if (children.length === 0) continue;
      const parentRow = rowOf[id] ?? 0;
      children.forEach((childId, i) => {
        if (rowOf[childId] === undefined) {
          rowOf[childId] = parentRow - (children.length - 1) / 2 + i;
        }
      });
    }
  }

  for (let d = maxDepth; d >= 1; d--) {
    const getParentRow = (id: string): number => {
      const parents = nodeIds.filter((p) => (depth[p] ?? 0) === d - 1 && (inputsOf[p] ?? []).includes(id));
      return parents.length > 0 ? Math.min(...parents.map((p) => rowOf[p] ?? 0)) : (rowOf[id] ?? 0);
    };

    const atDepth = nodeIds.filter((id) => (depth[id] ?? 0) === d);
    if (atDepth.length === 0) continue;

    atDepth.sort((a, b) => {
      const parentRowA = getParentRow(a);
      const parentRowB = getParentRow(b);
      return parentRowA !== parentRowB ? parentRowA - parentRowB : (rowOf[a] ?? 0) - (rowOf[b] ?? 0);
    });

    const centerBefore = atDepth.reduce((sum, id) => sum + (rowOf[id] ?? 0), 0) / atDepth.length;

    let minNextRow = -Infinity;
    for (const id of atDepth) {
      const assigned = Math.max(rowOf[id] ?? 0, minNextRow);
      rowOf[id] = assigned;
      minNextRow = assigned + 1;
    }

    const drift = atDepth.reduce((sum, id) => sum + (rowOf[id] ?? 0), 0) / atDepth.length - centerBefore;
    if (drift > 0) {
      for (const id of atDepth) {
        rowOf[id] = (rowOf[id] ?? 0) - drift;
      }
    }

    if (d - 1 >= 1) {
      for (const id of nodeIds) {
        if ((depth[id] ?? 0) !== d - 1) continue;
        const children = (inputsOf[id] ?? []).filter((childId) => (depth[childId] ?? 0) === d);
        if (children.length === 0) continue;
        const rows = children.map((childId) => rowOf[childId] ?? 0);
        rowOf[id] = (Math.min(...rows) + Math.max(...rows)) / 2;
      }
    }
  }

  const assignOutputRows = (id: string) => {
    const outputs = centreFunctional(
      (outputsOf[id] ?? []).filter((c) => (depth[c] ?? 0) < (depth[id] ?? 0)),
      functionalOutputsOf[id]
    );
    if (outputs.length === 0) return;
    const parentRow = rowOf[id] ?? 0;
    const total = outputs.length;
    outputs.forEach((outputId, i) => {
      if (rowOf[outputId] === undefined) {
        rowOf[outputId] = parentRow - (total - 1) / 2 + i;
        assignOutputRows(outputId);
      }
    });
  };

  const rootRow = rowOf[rootId] ?? 0;
  const consumerIds = nodeIds.filter((id) => (depth[id] ?? 0) === 0 && id !== rootId && rowOf[id] === undefined);

  const consumersByParent = new Map<string, string[]>();
  for (const id of consumerIds) {
    const parent = nodeIds.find((p) => (depth[p] ?? 0) === 1 && (outputsOf[p] ?? []).includes(id)) ?? '';
    consumersByParent.set(parent, [...(consumersByParent.get(parent) ?? []), id]);
  }

  // Closest-to-root first, so farther groups get pushed outward, not inward.
  const placeConsumerGroups = (parentIds: string[], direction: -1 | 1) => {
    parentIds.sort((a, b) => direction * ((rowOf[b] ?? 0) - (rowOf[a] ?? 0)));
    let boundary = rootRow;
    for (const parentId of parentIds) {
      const group = centreFunctional(consumersByParent.get(parentId) ?? [], functionalOutputsOf[parentId]);
      const groupSize = group.length;
      const parentRow = rowOf[parentId] ?? rootRow;
      if (direction === -1) {
        const idealBottom = parentRow + Math.floor((groupSize - 1) / 2);
        const bottom = Math.min(idealBottom, boundary - 1);
        const top = bottom - groupSize + 1;
        group.forEach((id, i) => {
          rowOf[id] = top + i;
        });
        boundary = top;
      } else {
        const idealTop = parentRow - Math.floor((groupSize - 1) / 2);
        const top = Math.max(idealTop, boundary + 1);
        group.forEach((id, i) => {
          rowOf[id] = top + i;
        });
        boundary = top + groupSize - 1;
      }
    }
  };

  const parentsAbove = [...consumersByParent.keys()].filter((p) => (rowOf[p] ?? rootRow) < rootRow);
  const parentsBelow = [...consumersByParent.keys()].filter((p) => (rowOf[p] ?? rootRow) >= rootRow);
  placeConsumerGroups(parentsAbove, -1);
  placeConsumerGroups(parentsBelow, 1);

  for (const id of nodeIds) {
    if ((depth[id] ?? 0) === 0) assignOutputRows(id);
  }

  let fallbackRow = Object.values(rowOf).length > 0 ? Math.max(...Object.values(rowOf)) + 1 : 0;
  for (const id of nodeIds) {
    if (rowOf[id] === undefined) rowOf[id] = fallbackRow++;
  }

  const minOutputDepth = Math.min(0, ...Object.values(depth));
  for (let d = minOutputDepth; d < 0; d++) {
    const primaryParentRow = (id: string): number => {
      const parents = nodeIds.filter((p) => (depth[p] ?? 0) === d + 1 && (outputsOf[p] ?? []).includes(id));
      return parents.length === 0 ? (rowOf[id] ?? 0) : Math.min(...parents.map((p) => rowOf[p] ?? 0));
    };

    const atDepth = nodeIds
      .filter((id) => (depth[id] ?? 0) === d)
      .sort((a, b) => {
        const pa = primaryParentRow(a);
        const pb = primaryParentRow(b);
        return pa !== pb ? pa - pb : (rowOf[a] ?? 0) - (rowOf[b] ?? 0);
      });

    let minNextRow = -Infinity;
    for (const id of atDepth) {
      const assigned = Math.max(rowOf[id] ?? 0, minNextRow);
      rowOf[id] = assigned;
      minNextRow = assigned + 1;
    }

    // Depth-0 rows are owned by the input-side layout.
    const atParentDepth = nodeIds.filter((id) => (depth[id] ?? 0) === d + 1 && (depth[id] ?? 0) < 0);
    for (const parentId of atParentDepth) {
      const children = (outputsOf[parentId] ?? []).filter((c) => (depth[c] ?? 0) === d);
      if (children.length === 0) continue;
      const childRows = children.map((c) => rowOf[c] ?? 0);
      rowOf[parentId] = (Math.min(...childRows) + Math.max(...childRows)) / 2;
    }
  }

  const positions: Record<string, { x: number; y: number }> = {};
  for (const id of nodeIds) {
    const d = depth[id] ?? 0;
    positions[id] = {
      x: (maxDepth - d) * (SUPPLY_NODE_WIDTH + SUPPLY_H_GAP),
      y: ((rowOf[id] ?? 0) - rootRow) * (nodeHeight + SUPPLY_V_SPACING),
    };
  }

  return { positions, depth };
};
